import { z } from 'zod';
import type { Verkaufsmodell } from './haendler';

/* ------------------------------------------------------------------ Grundwerte */

/** Auszahlbar ab Bestelldatum + Widerrufsfrist mit Puffer (MP_AUSZAHL_TAGE der PHP-Middleware) */
export const AUSZAHL_TAGE = 32;
/** Umsatzsteuer auf Provision und Warenwert (MP_UST) */
export const UST_SATZ = 19;

/** Provision in Cent: Betrag × Satz, kaufmännisch auf ganze Cent gerundet (Satz mit höchstens zwei Nachkommastellen) */
export function provisionCent(betrag: number, satz: number): number {
  const bp = Math.round(satz * 100);
  return Math.floor((betrag * bp + 5_000) / 10_000);
}

/** USt in Cent auf einen Nettobetrag, kaufmännisch gerundet */
export function ustCent(netto: number, satz = UST_SATZ): number {
  return Math.sign(netto) * Math.floor((Math.abs(netto) * satz + 50) / 100);
}

/** Dezimaltext oder Zahl („189.90“, 189.9, „1.234,50“) → Cent, ohne Umweg über Gleitkomma-Rundung */
export function centAus(wert: string | number): number | null {
  const t = String(wert).trim().replace(/\s|€/g, '');
  const normal = /^-?\d{1,3}(\.\d{3})*,\d+$|^-?\d+,\d+$/.test(t) ? t.replace(/\./g, '').replace(',', '.') : t;
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(normal);
  if (!m) return null;
  const rest = (m[3] ?? '').padEnd(3, '0');
  const cent = Number(m[2]) * 100 + Number(rest.slice(0, 2)) + (Number(rest[2]) >= 5 ? 1 : 0);
  return m[1] ? -cent : cent;
}

/* ------------------------------------------------------------------ Bestellungen */

export const BESTELL_STATUS = ['offen', 'versendet', 'zugestellt', 'storniert'] as const;
export const BestellStatus = z.enum(BESTELL_STATUS);
export type BestellStatus = z.infer<typeof BestellStatus>;

export const BESTELL_STATUS_TITEL: Record<BestellStatus, string> = {
  offen: 'Offen',
  versendet: 'Versendet',
  zugestellt: 'Zugestellt',
  storniert: 'Storniert',
};

/** Erlaubte Wechsel – Storno ist endgültig, Versand wird über die Sendungsdaten gemeldet */
export const BESTELL_WECHSEL: Record<BestellStatus, BestellStatus[]> = {
  offen: ['versendet', 'storniert'],
  versendet: ['zugestellt', 'storniert'],
  zugestellt: ['storniert'],
  storniert: [],
};

/** Gängige Versanddienstleister mit Adresse der Sendungsverfolgung (mp_carriers) */
export const VERSANDDIENSTE: Record<string, { name: string; url: string }> = {
  DHL: { name: 'DHL', url: 'https://www.dhl.de/de/privatkunden/dhl-sendungsverfolgung.html?piececode={nr}' },
  DPD: { name: 'DPD', url: 'https://tracking.dpd.de/status/de_DE/parcel/{nr}' },
  GLS: { name: 'GLS', url: 'https://gls-group.com/DE/de/paketverfolgung?match={nr}' },
  Hermes: { name: 'Hermes', url: 'https://www.myhermes.de/empfangen/sendungsverfolgung/sendungsinformation/#{nr}' },
  UPS: { name: 'UPS', url: 'https://www.ups.com/track?tracknum={nr}' },
  FedEx: { name: 'FedEx', url: 'https://www.fedex.com/fedextrack/?trknbr={nr}' },
  GO: { name: 'GO! Express', url: 'https://order.general-overnight.com/de/tracking?number={nr}' },
  Spedition: { name: 'Spedition', url: '' },
  Selbstabholung: { name: 'Selbstabholung', url: '' },
};

export function sendungUrl(dienst: string, nr: string): string {
  const d = VERSANDDIENSTE[dienst];
  return d && d.url && nr ? d.url.replace('{nr}', encodeURIComponent(nr)) : '';
}

const Datum = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als JJJJ-MM-TT');

export const BestellungAbfrage = z.object({
  q: z.string().trim().max(100).optional().transform((v) => v || undefined),
  status: BestellStatus.optional(),
  haendler: z.string().uuid().optional(),
  von: Datum.optional(),
  bis: Datum.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type BestellungAbfrage = z.infer<typeof BestellungAbfrage>;

export interface BestellungZeile {
  /** Teilbestellung (Bestellung je Händler) */
  id: string;
  nr: string;
  bestelltAm: string;
  haendlerId: string;
  haendlerFirma: string;
  kunde: string;
  status: BestellStatus;
  zahlung: string;
  positionen: number;
  /** Cent */
  summe: number;
  provision: number;
  sendungsnummer: string;
}

export type BestellungZaehler = Record<'alle' | BestellStatus, number>;

export interface BestellungSeite {
  eintraege: BestellungZeile[];
  weiter: string | null;
  gesamt: number;
  gesamtGenau: boolean;
  zaehler: BestellungZaehler;
}

export interface BestellPosition {
  id: string;
  titel: string;
  varianteTitel: string;
  sku: string;
  menge: number;
  /** Cent je Stück */
  preis: number;
  summe: number;
  provisionSatz: number;
  provision: number;
  produktId: string | null;
}

export interface Anschrift {
  name?: string;
  firma?: string;
  strasse?: string;
  zusatz?: string;
  plz?: string;
  ort?: string;
  land?: string;
  landCode?: string;
}

export interface BuchungZeile {
  id: string;
  typ: BuchungTyp;
  text: string;
  /** Cent, + = Guthaben des Händlers */
  betrag: number;
  valuta: string;
  gebuchtAm: string;
  bestellungNr: string;
  teilId: string | null;
  ausgeglichen: boolean;
  ausgleichId: string | null;
  von: string;
}

export interface BestellungDetail extends BestellungZeile {
  bestellungId: string;
  extId: string | null;
  quelle: string;
  waehrung: string;
  modell: Verkaufsmodell;
  provisionUst: number;
  /** Was der Händler erhält: Summe − Provision − USt auf Provision */
  netto: number;
  anschrift: Anschrift;
  statusGrund: string;
  statusAm: string | null;
  versandDienst: string;
  sendungUrl: string;
  versendetAm: string | null;
  zugestelltAm: string | null;
  /** Auszahlbar ab */
  faelligAm: string;
  /** Beleg der Auszahlung, mit der die Bestellung beglichen wurde */
  ausgezahlt: { id: string; belegNr: string | null; datum: string } | null;
  positionenListe: BestellPosition[];
  buchungen: BuchungZeile[];
  moeglich: BestellStatus[];
}

export const VersandEingabe = z.object({
  dienst: z.string().trim().max(40).default(''),
  sendungsnummer: z.string().trim().max(80).default(''),
  url: z.union([z.literal(''), z.string().trim().url('Bitte eine vollständige Adresse mit https:// angeben').max(500)]).default(''),
});
export type VersandEingabe = z.infer<typeof VersandEingabe>;

export const BestellStatusEingabe = z.object({
  status: z.enum(['zugestellt', 'storniert']),
  grund: z.string().trim().max(500).default(''),
});
export type BestellStatusEingabe = z.infer<typeof BestellStatusEingabe>;

/* ------------------------------------------------------------------ Buchungen */

export const BUCHUNG_TYPEN = {
  verkauf: 'Verkauf',
  provision: 'Provision Marktplatz',
  provision_ust: 'USt auf Provision',
  storno: 'Storno Verkauf',
  storno_provision: 'Storno Provision',
  storno_provision_ust: 'Storno USt auf Provision',
  auszahlung: 'Auszahlung',
  auszahlung_storno: 'Auszahlung zurückgenommen',
  korrektur: 'Korrektur',
} as const;
export type BuchungTyp = keyof typeof BUCHUNG_TYPEN;

/** Manuelle Buchung, z. B. Teilerstattung, Gutschrift oder Gebühr (mp_ledger_add) */
export const KorrekturEingabe = z.object({
  haendlerId: z.string().uuid(),
  /** Cent, positiv = Gutschrift, negativ = Belastung */
  betrag: z.number().int().refine((n) => n !== 0, 'Bitte einen Betrag angeben (positiv = Gutschrift, negativ = Belastung).')
    .refine((n) => Math.abs(n) <= 100_000_000, 'Höchstens 1.000.000 €'),
  text: z.string().trim().min(1, 'Bitte einen Buchungstext angeben – der Händler sieht ihn im Kontoauszug.').max(255),
  valuta: Datum.optional(),
  bestellungNr: z.string().trim().max(40).default(''),
});
export type KorrekturEingabe = z.infer<typeof KorrekturEingabe>;

export const BuchungAbfrage = z.object({
  haendler: z.string().uuid(),
  cursor: z.string().regex(/^\d{1,19}$/).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

export interface BuchungSeite {
  eintraege: BuchungZeile[];
  weiter: string | null;
}

/* ------------------------------------------------------------------ Auszahlungen */

export const AUSZAHLUNG_STATUS = ['vorbereitet', 'gebucht', 'storniert', 'verworfen'] as const;
export const AuszahlungStatus = z.enum(AUSZAHLUNG_STATUS);
export type AuszahlungStatus = z.infer<typeof AuszahlungStatus>;

export const AUSZAHLUNG_STATUS_TITEL: Record<AuszahlungStatus, string> = {
  vorbereitet: 'Vorbereitet',
  gebucht: 'Gebucht',
  storniert: 'Zurückgenommen',
  verworfen: 'Verworfen',
};

export const AUSZAHLUNG_METHODEN = { sepa: 'SEPA-Überweisung', stripe: 'Stripe', mollie: 'Mollie', nuvei: 'Nuvei', manuell: 'Manuell' } as const;
export type AuszahlungMethode = keyof typeof AUSZAHLUNG_METHODEN;

export const BELEG_ARTEN = { provisionsrechnung: 'Provisionsrechnung', gutschrift: 'Gutschrift', gemischt: 'Provisionsrechnung und Gutschrift' } as const;
export type BelegArt = keyof typeof BELEG_ARTEN;
export const BELEG_PRAEFIX: Record<BelegArt, string> = { provisionsrechnung: 'PR', gutschrift: 'GS', gemischt: 'AB' };

export const AuszahlungAbfrage = z.object({
  q: z.string().trim().max(80).optional().transform((v) => v || undefined),
  status: AuszahlungStatus.optional(),
  haendler: z.string().uuid().optional(),
  von: Datum.optional(),
  bis: Datum.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export interface AuszahlungZeile {
  id: string;
  haendlerId: string;
  haendlerFirma: string;
  /** Cent */
  betrag: number;
  datum: string;
  referenz: string;
  methode: AuszahlungMethode;
  status: AuszahlungStatus;
  belegNr: string | null;
  belegArt: BelegArt | null;
  erstelltAm: string;
}

export interface AuszahlungSeite {
  eintraege: AuszahlungZeile[];
  weiter: string | null;
  zaehler: Record<'alle' | AuszahlungStatus, number>;
}

export interface AuszahlungDetail extends AuszahlungZeile {
  externId: string;
  gebuchtAm: string | null;
  storniertAm: string | null;
  /** Buchungen, die diese Auszahlung ausgleicht (höchstens 500) */
  buchungen: BuchungZeile[];
  buchungenGesamt: number;
  summeBuchungen: number;
}

/** Saldo eines Händlers aus den offenen Buchungen (lg_balances) */
export interface HaendlerSaldo {
  haendlerId: string;
  firma: string;
  modell: Verkaufsmodell;
  stripeKonto: boolean;
  /** Konto beim Zahlungsanbieter des Marktplatzes (Stripe, Mollie oder Nuvei) eingerichtet */
  anbieterKonto: boolean;
  /** Cent: bis heute fällig */
  verfuegbar: number;
  /** Cent: noch in der Frist */
  wartet: number;
  gesamt: number;
  /** vorbereitete Auszahlung, falls vorhanden */
  vorbereitet: { id: string; betrag: number } | null;
}

export interface SaldenUebersicht {
  haendler: SaldenZeile[];
  summen: { verfuegbar: number; wartet: number; negativ: number; vorbereitet: number };
  fristTage: number;
  /** Zahlungsanbieter des Marktplatzes für Auszahlungen an Händler */
  anbieter: 'stripe' | 'mollie' | 'nuvei';
}
export type SaldenZeile = HaendlerSaldo;

export const AuszahlungVorbereiten = z.object({
  haendler: z.array(z.string().uuid()).min(1, 'Keine Händler ausgewählt.').max(1000),
  /** Cent je Händler aus der Ansicht – weicht der Saldo inzwischen ab, wird der Händler übersprungen */
  erwartet: z.record(z.string().uuid(), z.number().int()).default({}),
  datum: Datum.optional(),
  referenz: z.string().trim().max(80).default(''),
  methode: z.enum(['sepa', 'stripe', 'mollie', 'nuvei', 'manuell']).default('sepa'),
});
export type AuszahlungVorbereiten = z.infer<typeof AuszahlungVorbereiten>;

export interface LaufErgebnis {
  auszahlungen: { id: string; haendlerId: string; betrag: number }[];
  uebersprungen: { haendlerId: string; grund: string }[];
  summe: number;
}

/** Inhalt eines Abrechnungsbelegs (lg_beleg_data) – beim Festschreiben gespeichert, Beträge in Cent */
export interface BelegDaten {
  art: BelegArt;
  nr: string;
  datum: string;
  zeitraum: [string, string] | null;
  absender: { name: string; firma: string; strasse: string; ort: string; land: string; ustId: string; registergericht: string; geschaeftsfuehrung: string; email: string };
  empfaenger: { firma: string; strasse: string; ort: string; land: string; ustId: string; steuernummer: string };
  vermittlung: { nr: string; datum: string; umsatz: number; provision: number; ust: number; storno: boolean }[];
  eigenhandel: { nr: string; datum: string; brutto: number; netto: number; ust: number; storno: boolean }[];
  sonstige: { text: string; betrag: number }[];
  summen: { verkaufVermittlung: number; provisionNetto: number; provisionUst: number; eigenhandel: number };
  summeBuchungen: number;
  betrag: number;
  ustSatz: number;
}

export const AuszahlungFestschreiben =z.object({ ids: z.array(z.string().uuid()).min(1).max(1000) });

export interface FestschreibenErgebnis {
  gebucht: { id: string; belegNr: string }[];
  uebersprungen: { id: string; grund: string }[];
}
